"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  Send,
  Mail,
  MessageSquare,
  Smartphone,
  Loader2,
} from "lucide-react";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { StandardFormInput } from "@/components/common/StandardFormInput";
import { RichTextWithVariables } from "@/components/marketing/RichTextWithVariables";
import { toast } from "sonner";
// import type { MarketingAutomationTemplate, AutomationChannel } from "@/../../shiphouse-sdk/src/types/api-models";

// Define types locally since the import path is not working
interface MarketingAutomationTemplate {
  template?: string;
  trigger?: string;
  is_enabled?: boolean;
  schedule?: {
    delay_minutes?: number;
    delay_hours?: number;
    delay_days?: number;
  };
}

type AutomationChannel = "email" | "sms" | "whatsapp";

// Multi-select is implemented as custom component

interface AutomationForm {
  title: string;
  channels: string[];
  content: string;
  triggerType: string;
  triggerDelay: string;
  status: string;
}

const CHANNEL_OPTIONS = [
  {
    value: "email",
    label: "Email",
    icon: <Mail className="h-4 w-4" />,
    description: "Send via email",
  },
  {
    value: "sms",
    label: "SMS",
    icon: <Smartphone className="h-4 w-4" />,
    description: "Send via SMS",
  },
  {
    value: "whatsapp",
    label: "WhatsApp",
    icon: <MessageSquare className="h-4 w-4" />,
    description: "Send via WhatsApp",
  },
];

const TRIGGER_TYPES = [
  { value: "after_order", label: "After Order Placed" },
  { value: "after_payment", label: "After Payment Confirmed" },
  { value: "after_shipment", label: "After Order Shipped" },
  { value: "after_delivery", label: "After Order Delivered" },
  { value: "cart_abandonment", label: "Cart Abandonment" },
  { value: "customer_signup", label: "Customer Signup" },
];

const TRIGGER_DELAYS = [
  { value: "instantly", label: "Instantly" },
  { value: "15_minutes", label: "15 Minutes" },
  { value: "30_minutes", label: "30 Minutes" },
  { value: "1_hour", label: "1 Hour" },
  { value: "2_hours", label: "2 Hours" },
  { value: "4_hours", label: "4 Hours" },
  { value: "6_hours", label: "6 Hours" },
  { value: "12_hours", label: "12 Hours" },
  { value: "1_day", label: "1 Day" },
  { value: "2_days", label: "2 Days" },
  { value: "3_days", label: "3 Days" },
  { value: "1_week", label: "1 Week" },
];

const AutomationFormPage = () => {
  const navigate = useNavigate();
  const { storeType, storeId, automationId } = useParams<{
    storeType: string;
    storeId: string;
    automationId: string;
  }>();
  const isEdit = automationId && automationId !== "create";

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [, setAutomationData] = useState<{
    [key: string]: MarketingAutomationTemplate;
  }>({});

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AutomationForm>({
    defaultValues: {
      title: "",
      channels: [],
      content: "",
      triggerType: "",
      triggerDelay: "instantly",
      status: "draft",
    },
  });

  // Load existing automation data for edit mode
  useEffect(() => {
    if (isEdit && typeof automationId === "string") {
      loadAutomationData(automationId);
    }
  }, [isEdit, automationId]);

  const loadAutomationData = async (triggerName: string) => {
    setPageLoading(true);
    try {
      // For edit mode, we need to fetch data for all channels for this trigger
      // The API endpoint structure is: /api/v1/admin/{storeId}/communication/automation/{channelName}/{triggerName}
      const channels: AutomationChannel[] = ["email", "sms", "whatsapp"];
      const automationTemplates: {
        [key: string]: MarketingAutomationTemplate;
      } = {};

      for (const channel of channels) {
        try {
          const response = await fetch(
            `/api/v1/admin/${storeId}/communication/automation/${channel}/${triggerName}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            },
          );

          if (response.ok) {
            const template = await response.json();
            automationTemplates[channel] = template;
          }
        } catch (error) {
          console.warn(
            `Failed to load ${channel} template for ${triggerName}:`,
            error,
          );
        }
      }

      setAutomationData(automationTemplates);

      // Set form data based on the first available template
      const firstTemplate = Object.values(automationTemplates)[0];
      if (firstTemplate) {
        setValue("title", `${triggerName.replace(/_/g, " ")} Automation`);
        setValue("content", firstTemplate.template || "");
        setValue("triggerType", firstTemplate.trigger || triggerName);
        setValue("status", firstTemplate.is_enabled ? "active" : "draft");

        // Convert schedule to delay format
        if (firstTemplate.schedule) {
          const {
            delay_minutes = 0,
            delay_hours = 0,
            delay_days = 0,
          } = firstTemplate.schedule;
          let delayValue = "instantly";

          if (delay_days > 0) {
            if (delay_days === 1) delayValue = "1_day";
            else if (delay_days === 2) delayValue = "2_days";
            else if (delay_days === 3) delayValue = "3_days";
            else if (delay_days === 7) delayValue = "1_week";
          } else if (delay_hours > 0) {
            if (delay_hours === 12) delayValue = "12_hours";
            else if (delay_hours === 6) delayValue = "6_hours";
            else if (delay_hours === 4) delayValue = "4_hours";
            else if (delay_hours === 2) delayValue = "2_hours";
            else if (delay_hours === 1) delayValue = "1_hour";
          } else if (delay_minutes > 0) {
            if (delay_minutes === 30) delayValue = "30_minutes";
            else if (delay_minutes === 15) delayValue = "15_minutes";
          }

          setValue("triggerDelay", delayValue);
        }

        // Set enabled channels
        const enabledChannels = Object.entries(automationTemplates)
          .filter(([, template]) => template.is_enabled)
          .map(([channel]) => channel);

        setSelectedChannels(enabledChannels);
        setValue("channels", enabledChannels);
      }
    } catch (error) {
      console.error("Failed to load automation data:", error);
      toast.error("Failed to load automation data. Please try again.");
    } finally {
      setPageLoading(false);
    }
  };

  const onSubmit = async (data: AutomationForm) => {
    setLoading(true);

    try {
      const { triggerDelay, triggerType, content, status } = data;

      // Convert delay to schedule format
      const schedule = convertDelayToSchedule(triggerDelay);

      // Update automation for each selected channel
      const updatePromises = selectedChannels.map(async (channel) => {
        const updateData = {
          template: content,
          subject: channel === "email" ? data.title : undefined,
          is_enabled: status === "active",
          schedule: schedule,
        };

        const response = await fetch(
          `/api/v1/admin/${storeId}/communication/automation/${channel}/${triggerType}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updateData),
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to update ${channel} automation`);
        }

        return response.json();
      });

      await Promise.all(updatePromises);

      toast.success(
        `Automation ${isEdit ? "updated" : "created"} successfully`,
      );

      // Navigate back to automations list
      navigate(`/${storeType}/${storeId}/marketing/automation`);
    } catch (error) {
      console.error("Failed to save automation:", error);
      toast.error("Failed to save automation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const convertDelayToSchedule = (delay: string) => {
    const schedule = {
      delay_minutes: 0,
      delay_hours: 0,
      delay_days: 0,
    };

    switch (delay) {
      case "15_minutes":
        schedule.delay_minutes = 15;
        break;
      case "30_minutes":
        schedule.delay_minutes = 30;
        break;
      case "1_hour":
        schedule.delay_hours = 1;
        break;
      case "2_hours":
        schedule.delay_hours = 2;
        break;
      case "4_hours":
        schedule.delay_hours = 4;
        break;
      case "6_hours":
        schedule.delay_hours = 6;
        break;
      case "12_hours":
        schedule.delay_hours = 12;
        break;
      case "1_day":
        schedule.delay_days = 1;
        break;
      case "2_days":
        schedule.delay_days = 2;
        break;
      case "3_days":
        schedule.delay_days = 3;
        break;
      case "1_week":
        schedule.delay_days = 7;
        break;
      default: // "instantly"
        break;
    }

    return schedule;
  };

  const handleSaveDraft = () => {
    setValue("status", "draft");
    handleSubmit(onSubmit)();
  };

  const handleActivate = () => {
    setValue("status", "active");
    handleSubmit(onSubmit)();
  };

  const breadcrumbItems = [
    {
      label: "Marketing",
      href: `/${storeType}/${storeId}/marketing`,
    },
    {
      label: "Automation",
      href: `/${storeType}/${storeId}/marketing/automation`,
    },
    {
      label: isEdit ? "Edit Automation" : "New Automation",
      isActive: true,
    },
  ];

  return (
    <div className="space-y-4">
      <Breadcrumb items={breadcrumbItems} showBackButton={false} />

      {pageLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="text-lg text-muted-foreground">
              Loading automation data...
            </span>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to={`/${storeType}/${storeId}/marketing/automation`}>
                <Button variant="outline" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              </Link>
              <h1 className="text-2xl font-semibold">
                {isEdit ? "Edit Automation" : "Create New Automation"}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={loading}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Draft
              </Button>
              <Button type="button" onClick={handleActivate} disabled={loading}>
                <Send className="h-4 w-4 mr-2" />
                {isEdit ? "Update & Activate" : "Create & Activate"}
              </Button>
            </div>
          </div>

          {/* Form Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-medium mb-4">Basic Information</h3>

                <div className="space-y-4">
                  <StandardFormInput
                    label="Automation Title"
                    placeholder="e.g., Welcome New Customers"
                    error={errors.title?.message}
                    required
                    {...register("title", { required: "Title is required" })}
                  />

                  <div className="space-y-2">
                    <label className="block text-[13px] font-normal text-muted-foreground">
                      Channels
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {CHANNEL_OPTIONS.map((channel) => (
                        <div
                          key={channel.value}
                          className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                            selectedChannels.includes(channel.value)
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50"
                          }`}
                          onClick={() => {
                            const newChannels = selectedChannels.includes(
                              channel.value,
                            )
                              ? selectedChannels.filter(
                                  (c) => c !== channel.value,
                                )
                              : [...selectedChannels, channel.value];
                            setSelectedChannels(newChannels);
                            setValue("channels", newChannels);
                          }}
                        >
                          {selectedChannels.includes(channel.value) && (
                            <div className="absolute top-2 right-2">
                              <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white rounded-full" />
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-3">
                            <div
                              className={`p-2 rounded-lg ${
                                selectedChannels.includes(channel.value)
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {channel.icon}
                            </div>
                            <div>
                              <h4 className="font-medium">{channel.label}</h4>
                              <p className="text-xs text-muted-foreground">
                                {channel.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedChannels.length === 0 && (
                      <p className="text-red-500 text-xs">
                        At least one channel is required
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-medium mb-4">Message Content</h3>

                <Controller
                  name="content"
                  control={control}
                  rules={{ required: "Message content is required" }}
                  render={({ field }) => (
                    <RichTextWithVariables
                      label="Message Content"
                      required
                      value={String(field.value || "")}
                      onChange={field.onChange}
                      placeholder="Write your automation message here..."
                      height={200}
                      error={errors.content?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* Sidebar Settings */}
            <div className="space-y-6">
              {/* Trigger Settings */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-medium mb-4">Trigger Settings</h3>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-[13px] font-normal text-muted-foreground">
                      Trigger Type <span className="text-red-500 ml-1">*</span>
                    </label>
                    <Controller
                      name="triggerType"
                      control={control}
                      rules={{ required: "Trigger type is required" }}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger className="bg-background border-border hover:bg-accent">
                            <SelectValue placeholder="Select trigger type" />
                          </SelectTrigger>
                          <SelectContent className="bg-background border-border">
                            {TRIGGER_TYPES.map((trigger) => (
                              <SelectItem
                                key={trigger.value}
                                value={trigger.value}
                                className="hover:bg-accent"
                              >
                                {trigger.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.triggerType && (
                      <p className="text-red-500 text-xs">
                        {errors.triggerType.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[13px] font-normal text-muted-foreground">
                      Send After
                    </label>
                    <Controller
                      name="triggerDelay"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger className="bg-background border-border hover:bg-accent">
                            <SelectValue placeholder="Select delay" />
                          </SelectTrigger>
                          <SelectContent className="bg-background border-border">
                            {TRIGGER_DELAYS.map((delay) => (
                              <SelectItem
                                key={delay.value}
                                value={delay.value}
                                className="hover:bg-accent"
                              >
                                {delay.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-medium mb-4">Preview</h3>

                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium">Channels:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedChannels.map((channel) => {
                        const channelInfo = CHANNEL_OPTIONS.find(
                          (c) => c.value === channel,
                        );
                        return (
                          <Badge
                            key={channel}
                            variant={channel as "email" | "sms" | "whatsapp"}
                          >
                            {channelInfo?.label}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <span className="text-sm font-medium">Trigger:</span>
                    <p className="text-sm text-muted-foreground mt-1">
                      {watch("triggerType") && watch("triggerDelay") && (
                        <>
                          {
                            TRIGGER_TYPES.find(
                              (t) => t.value === watch("triggerType"),
                            )?.label
                          }{" "}
                          •{" "}
                          {
                            TRIGGER_DELAYS.find(
                              (d) => d.value === watch("triggerDelay"),
                            )?.label
                          }
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default AutomationFormPage;
